import React from 'react';
import { API_BASE_URL } from '../api/config';

/** Resolve a product image link (e.g. "/v1/product/12/image") against the API
 *  base. Product images are served via the cookie media-session, so a plain
 *  <img> works (unlike the encrypted face-scan images that need AuthImage). */
const normalizeImage = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  try {
    return new URL(url.startsWith('/') ? url : `/${url}`, API_BASE_URL).toString();
  } catch {
    return null;
  }
};

interface Props {
  imageUrlA?: string | null;
  imageUrlB?: string | null;
  alt?: string;
  /** Render a single product image filling the tile (single-product scans). */
  single?: boolean;
}

/** A product history thumbnail: a single product image (`single`) or a split
 *  two-image tile for comparison/compatibility (products A and B side by side),
 *  mirroring the single `.hist-thumb`. */
export function ProductPairThumbnail({ imageUrlA, imageUrlB, alt, single }: Props): React.JSX.Element {
  const a = normalizeImage(imageUrlA);
  const b = normalizeImage(imageUrlB);

  const Half = ({ src }: { src: string | null }) => (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--c-sand, #eee)',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : null}
    </span>
  );

  if (single) {
    return (
      <div className="hist-thumb" aria-label={alt ?? 'Product'} style={{ overflow: 'hidden' }}>
        <Half src={a} />
      </div>
    );
  }

  return (
    <div
      className="hist-thumb hist-thumb--pair"
      aria-label={alt ?? 'Product pair'}
      style={{ display: 'flex', gap: 1, overflow: 'hidden' }}
    >
      <Half src={a} />
      <Half src={b} />
    </div>
  );
}

export default ProductPairThumbnail;
